import { PDFDocument } from 'src/assets/js/pdf-lib.min.js';

// Security (#205111): content checks for files picked in the common file uploader.
// They run AFTER the extension has passed the screen JSON's `formatsAllowed`; that
// extension decides which rule applies. Each rule verifies that
//   1. the bytes really are that kind of file (an .exe / .html renamed to .pdf or .jpg is rejected), and
//   2. the file carries no executable content (PDF JavaScript/Launch, Office macros,
//      scripts in SVG/HTML, executables inside a zip).
// Only extensions in INSPECTED_EXTENSIONS are inspected; anything else passes through unchanged.

export interface UploadInspection {
  allowed: boolean;
  reason?: string;
}

export const UPLOAD_REJECT_REASON = {
  MISMATCH: 'File content does not match its extension',
  SCRIPT: 'Contains embedded script or executable content',
  MACRO: 'Contains macros',
  ARCHIVE: 'Archive contains executable files',
  UNREADABLE: 'File could not be verified'
};

type Family = 'image' | 'pdf' | 'office' | 'odf' | 'markup' | 'text' | 'zip' | 'media';

const FAMILY_BY_EXT: { [ext: string]: Family } = {
  jpg: 'image', jpeg: 'image', jfif: 'image', pjpeg: 'image', pjp: 'image', png: 'image', apng: 'image',
  gif: 'image', bmp: 'image', webp: 'image', tif: 'image', tiff: 'image', ico: 'image', cur: 'image',
  heic: 'image', heif: 'image', avif: 'image',
  pdf: 'pdf',
  doc: 'office', dot: 'office', docx: 'office', dotx: 'office',
  xls: 'office', xlt: 'office', xlsx: 'office', xltx: 'office',
  ppt: 'office', pps: 'office', pptx: 'office', ppsx: 'office', potx: 'office', rtf: 'office',
  odt: 'odf', ods: 'odf', odp: 'odf', odg: 'odf',
  svg: 'markup', html: 'markup', htm: 'markup', xhtml: 'markup',
  txt: 'text', csv: 'text', tsv: 'text', json: 'text', xml: 'text', jrxml: 'text', log: 'text', md: 'text',
  zip: 'zip',
  mp3: 'media', mp4: 'media', m4a: 'media', m4v: 'media', mov: 'media', '3gp': 'media',
  aac: 'media', wav: 'media', webm: 'media', ogg: 'media', oga: 'media', ogv: 'media'
};

// Extensions inspected: every extension with a content rule above, plus the other formats
// screens allow in `formatsAllowed` (certificates, DICOM, plist ...), which have no format
// rule and are only screened for disguised executables. Matching is case-insensitive.
const INSPECTED_EXTENSIONS = [
  ...Object.keys(FAMILY_BY_EXT),
  'cer', 'crt', 'pem', 'p7b', 'p12', 'pfx', 'jks', 'p8', 'dcm', 'plist'
];

export function requiresInspection(extension: string): boolean {
  return INSPECTED_EXTENSIONS.includes(normalizeExtension(extension));
}

function normalizeExtension(extension: string): string {
  return (extension || '').replace(/^\./, '').toLowerCase();
}

const ALLOWED: UploadInspection = { allowed: true };
const reject = (reason: string): UploadInspection => ({ allowed: false, reason });

// Only the leading bytes are needed for signature-only checks (images, media, unknown types).
const HEADER_BYTES = 4096;
const TEXT_PROBE_BYTES = 65536;

/**
 * Inspects a file whose extension already passed `formatsAllowed`.
 * Extensions outside INSPECTED_EXTENSIONS are allowed without reading the file.
 * Never throws: a read/parse failure resolves to a rejection (fail closed).
 */
export async function inspectUploadFile(file: Blob, extension: string): Promise<UploadInspection> {
  if (!requiresInspection(extension)) {
    return ALLOWED;
  }
  try {
    const family = FAMILY_BY_EXT[normalizeExtension(extension)];
    switch (family) {
      case 'image': return checkImage(await readBytes(file, HEADER_BYTES));
      case 'media': return checkMedia(await readBytes(file, HEADER_BYTES));
      case 'pdf': return await checkPdf(await readBytes(file));
      case 'office': return checkOffice(await readBytes(file));
      case 'odf': return checkOdf(await readBytes(file));
      case 'markup': return checkMarkup(await readBytes(file));
      case 'text': return checkText(await readBytes(file, TEXT_PROBE_BYTES));
      case 'zip': return checkZip(await readBytes(file));
      default: return isExecutable(await readBytes(file, HEADER_BYTES)) ? reject(UPLOAD_REJECT_REASON.MISMATCH) : ALLOWED;
    }
  } catch (err) {
    console.error('inspectUploadFile: unable to inspect file', err);
    return reject(UPLOAD_REJECT_REASON.UNREADABLE);
  }
}

// ---------------------------------------------------------------- family rules

function checkImage(b: Uint8Array): UploadInspection {
  // Any real image signature is accepted for any image extension - a PNG saved as .jpg
  // is common and harmless; what matters is that it is not a script/binary in disguise.
  const isImage = startsWith(b, [0xFF, 0xD8, 0xFF])                                  // jpeg
    || startsWith(b, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A])               // png / apng
    || ascii(b, 0, 6) === 'GIF87a' || ascii(b, 0, 6) === 'GIF89a'
    || ascii(b, 0, 2) === 'BM'
    || (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP')
    || startsWith(b, [0x49, 0x49, 0x2A, 0x00]) || startsWith(b, [0x4D, 0x4D, 0x00, 0x2A]) // tiff
    || startsWith(b, [0x00, 0x00, 0x01, 0x00]) || startsWith(b, [0x00, 0x00, 0x02, 0x00]) // ico / cur
    || (ascii(b, 4, 4) === 'ftyp' && ['heic', 'heix', 'hevc', 'heim', 'heis', 'mif1', 'msf1', 'avif', 'avis'].includes(ascii(b, 8, 4)));
  return isImage ? ALLOWED : reject(UPLOAD_REJECT_REASON.MISMATCH);
}

function checkMedia(b: Uint8Array): UploadInspection {
  const box = ascii(b, 4, 4);
  const isMedia = ['ftyp', 'moov', 'mdat', 'wide', 'free', 'skip'].includes(box)       // mp4 / m4a / mov / 3gp
    || ascii(b, 0, 3) === 'ID3'                                                         // mp3 with tag
    || (b.length > 1 && b[0] === 0xFF && (b[1] & 0xE0) === 0xE0)                        // mp3 / aac frame sync
    || ((ascii(b, 0, 4) === 'RIFF' || ascii(b, 0, 4) === 'RIFX') && ascii(b, 8, 4) === 'WAVE')
    || startsWith(b, [0x1A, 0x45, 0xDF, 0xA3])                                          // webm / mkv
    || ascii(b, 0, 4) === 'OggS';
  return isMedia ? ALLOWED : reject(UPLOAD_REJECT_REASON.MISMATCH);
}

function checkOffice(b: Uint8Array): UploadInspection {
  // doc/xls/ppt and docx/xlsx/pptx are routinely swapped (and a password-protected
  // .docx is an OLE file), so any Office container is accepted for any Office extension.
  if (startsWith(b, OLE_SIGNATURE)) {
    // VBA lives in a "_VBA_PROJECT" stream (Word: Macros/VBA, Excel: _VBA_PROJECT_CUR/VBA);
    // OLE directory names are UTF-16LE.
    return latin1(b).includes(utf16le('_VBA_PROJECT')) ? reject(UPLOAD_REJECT_REASON.MACRO) : ALLOWED;
  }
  if (isZip(b)) {
    const entries = zipEntryNames(b);
    if (entries.includes('mimetype')) {
      return odfHasMacro(entries) ? reject(UPLOAD_REJECT_REASON.MACRO) : ALLOWED;  // .odt saved as .doc
    }
    if (!entries.includes('[content_types].xml')) {
      return reject(UPLOAD_REJECT_REASON.MISMATCH);
    }
    const hasMacro = entries.some((name) => name.endsWith('vbaproject.bin') || name.includes('/activex/') || name.includes('/macrosheets/'));
    return hasMacro ? reject(UPLOAD_REJECT_REASON.MACRO) : ALLOWED;
  }
  if (ascii(b, 0, 5) === '{\\rtf') {
    // Embedded OLE objects are the usual RTF exploit carrier.
    return /\\obj(data|update|emb|link|autlink)\b/i.test(latin1(b)) ? reject(UPLOAD_REJECT_REASON.SCRIPT) : ALLOWED;
  }
  if (isText(b)) {
    // Many systems export "Excel" as an HTML/XML/CSV table saved with an .xls extension.
    return hasMarkupScript(decodeText(b)) ? reject(UPLOAD_REJECT_REASON.SCRIPT) : ALLOWED;
  }
  return reject(UPLOAD_REJECT_REASON.MISMATCH);
}

function checkOdf(b: Uint8Array): UploadInspection {
  if (!isZip(b)) {
    return reject(UPLOAD_REJECT_REASON.MISMATCH);
  }
  return odfHasMacro(zipEntryNames(b)) ? reject(UPLOAD_REJECT_REASON.MACRO) : ALLOWED;
}

function odfHasMacro(entries: string[]): boolean {
  // Basic macros: Basic/<library>/<module>.xml (script-lb.xml / script-lc.xml are just indexes).
  // Other script languages: Scripts/<language>/...
  return entries.some((name) =>
    (name.startsWith('basic/') && name.endsWith('.xml') && !/\/script-l[bc]\.xml$/.test(name) && name.split('/').length > 2)
    || (name.startsWith('scripts/') && !name.endsWith('/')));
}

function checkMarkup(b: Uint8Array): UploadInspection {
  if (!isText(b)) {
    return reject(UPLOAD_REJECT_REASON.MISMATCH);
  }
  return hasMarkupScript(decodeText(b)) ? reject(UPLOAD_REJECT_REASON.SCRIPT) : ALLOWED;
}

function checkText(b: Uint8Array): UploadInspection {
  return isText(b) ? ALLOWED : reject(UPLOAD_REJECT_REASON.MISMATCH);
}

function checkZip(b: Uint8Array): UploadInspection {
  if (!isZip(b)) {
    return reject(UPLOAD_REJECT_REASON.MISMATCH);
  }
  return zipEntryNames(b).some((name) => EXECUTABLE_EXT_RE.test(name)) ? reject(UPLOAD_REJECT_REASON.ARCHIVE) : ALLOWED;
}

// ---------------------------------------------------------------- PDF

// Names that mean executable / dangerous content anywhere in a PDF. A name may hex-escape
// any char (/J#61vaScript == /JavaScript), so raw bytes are normalised before matching.
const PDF_ACTIVE_NAMES = ['JavaScript', 'JS', 'Launch', 'RichMedia', 'RichMediaExecute', 'EmbeddedFile', 'EmbeddedFiles', 'ImportData', 'SubmitForm', 'GoToE', 'XFA'];
const PDF_ACTIVE_NAME_RE = new RegExp('/(' + PDF_ACTIVE_NAMES.join('|') + ')(?=[\\s/<>\\[\\]()%]|$)');
const PDF_ACTIVE_KEYS = new Set(['/JS', '/JavaScript', '/EmbeddedFile', '/EmbeddedFiles', '/RichMedia', '/RichMediaContent', '/XFA']);
const PDF_ACTIVE_ACTIONS = new Set(['/JavaScript', '/Launch', '/ImportData', '/SubmitForm', '/RichMediaExecute', '/GoToE']);
const SCRIPT_URI_RE = /^\s*(javascript|vbscript|data|file):/i;
// Real PDFs nest direct objects a handful of levels deep; anything deeper is crafted to evade the walk.
const PDF_MAX_DEPTH = 32;

async function checkPdf(b: Uint8Array): Promise<UploadInspection> {
  // Readers accept the header anywhere in the first 1 KB.
  if (latin1(b.subarray(0, 1024)).indexOf('%PDF-') === -1) {
    return reject(UPLOAD_REJECT_REASON.MISMATCH);
  }
  return (await pdfHasActiveContent(b)) ? reject(UPLOAD_REJECT_REASON.SCRIPT) : ALLOWED;
}

/**
 * Two passes: a raw scan of the object syntax (also covers files pdf-lib cannot parse)
 * and a pdf-lib walk of every indirect object (covers objects packed inside compressed
 * object streams, which the raw scan cannot see).
 */
async function pdfHasActiveContent(b: Uint8Array): Promise<boolean> {
  // Drop stream bodies: compressed binary would randomly contain "/JS " and give false positives.
  const objectSyntax = decodePdfNames(latin1(b).replace(/stream(?:\r\n|\r|\n)[\s\S]*?endstream/g, ''));
  if (PDF_ACTIVE_NAME_RE.test(objectSyntax) || /\/URI\s*\(\s*(javascript|vbscript|data|file):/i.test(objectSyntax)) {
    return true;
  }

  let doc: any;
  try {
    doc = await PDFDocument.load(b, { ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false });
  } catch (err) {
    // pdf-lib is stricter than browser viewers; for a file it cannot parse the raw scan is the verdict.
    console.warn('pdfHasActiveContent: pdf-lib could not parse file, raw scan only', err);
    return false;
  }
  for (const [, obj] of doc.context.enumerateIndirectObjects()) {
    if (pdfObjectIsActive(obj, 0)) {
      return true;
    }
  }
  return false;
}

// Duck-typed over pdf-lib objects: PDFDict.dict is a Map, PDFStream.dict is a PDFDict,
// PDFArray.array is an array, PDFName.toString() is the canonical "/Name",
// PDFString / PDFHexString expose decodeText().
function pdfObjectIsActive(obj: any, depth: number): boolean {
  if (!obj) {
    return false;
  }
  if (depth > PDF_MAX_DEPTH) {
    return true;
  }
  if (Array.isArray(obj.array)) {
    return obj.array.some((item) => pdfObjectIsActive(item, depth + 1));
  }
  const map: Map<any, any> = obj.dict instanceof Map ? obj.dict : obj.dict?.dict;
  if (!(map instanceof Map)) {
    return false;
  }
  for (const [key, value] of map) {
    const k = String(key);
    if (PDF_ACTIVE_KEYS.has(k)) {
      return true;
    }
    if (k === '/S' && PDF_ACTIVE_ACTIONS.has(String(value))) {
      return true;
    }
    if (k === '/URI' && typeof value?.decodeText === 'function' && SCRIPT_URI_RE.test(value.decodeText())) {
      return true;
    }
    if (pdfObjectIsActive(value, depth + 1)) {
      return true;
    }
  }
  return false;
}

function decodePdfNames(text: string): string {
  return text.replace(/\/[^\s\/<>\[\]()%{}]*#[0-9A-Fa-f]{2}[^\s\/<>\[\]()%{}]*/g,
    (name) => name.replace(/#([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))));
}

// ---------------------------------------------------------------- markup / text

// Deliberately NOT flagged, because legitimate files use them: <meta> (Excel HTML export),
// <!ENTITY> (Illustrator SVG; entity text is scanned anyway), <foreignObject> (draw.io SVG).
const MARKUP_SCRIPT_RES = [
  /<script\b/i,
  /<(iframe|frame|embed|object|applet)\b/i,
  /<[^>]*[\s"'\/]on[a-z]+\s*=/i,                       // inline event handler inside a tag: onload=, onclick= ...
  /(j\s*a\s*v\s*a|v\s*b)\s*s\s*c\s*r\s*i\s*p\s*t\s*:/i, // javascript: / vbscript: (browsers ignore whitespace inside)
  /data\s*:\s*text\/html/i
];

function hasMarkupScript(text: string): boolean {
  // Decode numeric character references so "&#106;avascript:" cannot slip past.
  const decoded = text.replace(/&#(x[0-9a-f]+|\d+);?/gi, (_, code) => {
    const n = code[0].toLowerCase() === 'x' ? parseInt(code.slice(1), 16) : parseInt(code, 10);
    return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
  });
  return MARKUP_SCRIPT_RES.some((re) => re.test(decoded));
}

function isText(b: Uint8Array): boolean {
  if (hasUtf16Bom(b)) {
    return true;
  }
  const probe = b.subarray(0, TEXT_PROBE_BYTES);
  return probe.indexOf(0) === -1;
}

function decodeText(b: Uint8Array): string {
  if (startsWith(b, [0xFF, 0xFE])) {
    return new TextDecoder('utf-16le').decode(b);
  }
  if (startsWith(b, [0xFE, 0xFF])) {
    return new TextDecoder('utf-16be').decode(b);
  }
  return new TextDecoder('utf-8').decode(b);
}

function hasUtf16Bom(b: Uint8Array): boolean {
  return startsWith(b, [0xFF, 0xFE]) || startsWith(b, [0xFE, 0xFF]);
}

// ---------------------------------------------------------------- binary helpers

const OLE_SIGNATURE = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];
const EXECUTABLE_EXT_RE = /\.(exe|dll|scr|com|pif|bat|cmd|msi|msp|msc|ps1|psm1|vbs|vbe|js|jse|wsf|wsh|hta|lnk|jar|sh|apk|reg|cpl|inf|gadget)$/i;

function isExecutable(b: Uint8Array): boolean {
  return ascii(b, 0, 2) === 'MZ'                                       // Windows PE
    || startsWith(b, [0x7F, 0x45, 0x4C, 0x46])                         // ELF
    || startsWith(b, [0xFE, 0xED, 0xFA, 0xCE]) || startsWith(b, [0xFE, 0xED, 0xFA, 0xCF])
    || startsWith(b, [0xCE, 0xFA, 0xED, 0xFE]) || startsWith(b, [0xCF, 0xFA, 0xED, 0xFE]) // Mach-O
    || ascii(b, 0, 2) === '#!';                                        // shell script
}

function isZip(b: Uint8Array): boolean {
  return startsWith(b, [0x50, 0x4B, 0x03, 0x04]) || startsWith(b, [0x50, 0x4B, 0x05, 0x06]);
}

/**
 * Lower-cased entry names from both local headers and the central directory. Reading
 * both defeats archives whose two listings disagree; names are stored uncompressed,
 * so no inflating is needed.
 */
function zipEntryNames(b: Uint8Array): string[] {
  const names = new Set<string>();
  for (let i = 0; i + 46 <= b.length; i++) {
    if (b[i] !== 0x50 || b[i + 1] !== 0x4B) {
      continue;
    }
    let nameOffset = -1;
    let nameLength = 0;
    if (b[i + 2] === 0x03 && b[i + 3] === 0x04) {          // local file header
      nameLength = b[i + 26] | (b[i + 27] << 8);
      nameOffset = i + 30;
    } else if (b[i + 2] === 0x01 && b[i + 3] === 0x02) {   // central directory entry
      nameLength = b[i + 28] | (b[i + 29] << 8);
      nameOffset = i + 46;
    }
    if (nameOffset > 0 && nameLength > 0 && nameOffset + nameLength <= b.length) {
      names.add(latin1(b.subarray(nameOffset, nameOffset + nameLength)).replace(/\\/g, '/').toLowerCase());
    }
  }
  return Array.from(names);
}

// FileReader instead of Blob.arrayBuffer(): the latter is missing on older Android WebViews / iOS,
// and since inspection fails closed that would reject every upload on those devices.
function readBytes(file: Blob, limit?: number): Promise<Uint8Array> {
  const part = limit && file.size > limit ? file.slice(0, limit) : file;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(part);
  });
}

function startsWith(b: Uint8Array, sig: number[], offset = 0): boolean {
  if (b.length < offset + sig.length) {
    return false;
  }
  return sig.every((byte, i) => b[offset + i] === byte);
}

function ascii(b: Uint8Array, offset: number, length: number): string {
  return b.length < offset + length ? '' : latin1(b.subarray(offset, offset + length));
}

// latin1 keeps a 1:1 byte -> char mapping, so binary content cannot break string searches.
function latin1(b: Uint8Array): string {
  let text = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < b.length; i += CHUNK) {
    text += String.fromCharCode.apply(null, b.subarray(i, i + CHUNK) as any);
  }
  return text;
}

function utf16le(text: string): string {
  return text.split('').map((ch) => ch + '\u0000').join('');
}
